/**
 * The three texts a new member agrees to, as the legacy enrolment form showed
 * them: the membership terms, the data privacy policy, and the reasons personal
 * data is used for.
 *
 * Legal text, not interface copy — it is not put through the dictionary. The
 * words a person agreed to have to be the words on file, in the language they
 * were written in, so the English stands whatever the screen language is.
 */

import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";

export type ConsentText = "terms" | "privacy" | "reasons";

const TITLES: Record<ConsentText, string> = {
  terms: "S&R Membership Shopping",
  privacy: "Data Privacy Policy",
  reasons: "Data Privacy Policy",
};

export function ConsentTextModal({
  text,
  onClose,
}: {
  text: ConsentText | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open={text !== null}
      onClose={onClose}
      size="lg"
      title={text ? TITLES[text] : ""}
      description={text === "terms" ? "Gold Member" : undefined}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="text-fg text-callout max-h-[60vh] overflow-y-auto pr-2 leading-relaxed [&_h3]:mt-5 [&_h3]:mb-1.5 [&_h3]:font-semibold [&_li]:mt-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mt-2 [&_ul]:list-disc [&_ul]:pl-6">
        {text === "terms" ? <Terms /> : text === "privacy" ? <Privacy /> : <Reasons />}
      </div>
    </Modal>
  );
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section>
    <h3>{title}</h3>
    {children}
  </section>
);

function Terms() {
  return (
    <>
      <p>
        We look forward to serving you as a new member of S&R Membership Shopping. Your
        membership privileges and conditions are described below.
      </p>
      <Section title="1. Membership">
        <p>
          Membership is available to all qualified individuals 18 years of age and above. S&R
          Membership Shopping reserves the right to refuse membership to any applicant.
          Membership is subject to the rules adopted by S&R Membership Shopping and these rules
          may be amended without prior notice. Membership is revocable without notice or cause.
        </p>
      </Section>
      <Section title="2. Membership Cards and Fees">
        <p>
          Membership fee is for a twelve (12)-month period from the date of enrollment of the
          primary cardholder.
        </p>
        <p>
          Both primary and extension will expire on the anniversary date of the primary
          cardholder. Your card is valid at any S&R Membership Shopping location.
        </p>
        <p>
          You will be requested to show your membership card when entering S&R clubs and when
          paying at the register. Report lost or stolen card to the S&R Membership Department. A
          P250 fee will be charged for the replacement of each card. Memberships may be
          terminated at the discretion of S&R Membership Shopping. All membership cards are
          non-transferable, remain the property of S&R Membership Shopping and must be returned
          upon request.
        </p>
      </Section>
      <Section title="3. Renewing, Adding or Deleting Cards">
        <p>
          The primary member must authorize renewal or cardholder changes, including any
          addition or deletion made, and is responsible for the membership. You will be notified
          of upcoming renewal each year. Membership renewal fee must be paid by all cardholders
          at the time of renewal and can be done at any S&R location. Membership will be
          extended for an additional 12 months (1 year) from expiration date upon receipt of
          renewal fees or from your applicable expiration date, whichever is longer. You will
          not receive new cards with each renewal. You will continue using your original
          membership card unless otherwise advised by S&R Membership Shopping.
        </p>
      </Section>
      <Section title="4. Termination of Membership">
        <p>
          S&R Membership Shopping will refund your membership fee at any time if you are
          dissatisfied. The primary cardholder may submit a written termination notice together
          with membership card, official receipt and any valid identification card with photo to
          S&R Membership Department. Refunds will be prorated based on the number of months
          remaining in your current annual membership.
        </p>
      </Section>
      <Section title="5. Payment">
        <p>
          Payment can be made via cash, debit card, credit card, gift card and any other payment
          authorized by S&R Membership Shopping.
        </p>
      </Section>
      <Section title="6. Agreement on Return or Refund">
        <p>
          The member, his/her extension, and S&R agree that a return or refund shall be governed
          by the following rules:
        </p>
        <ul>
          <li>
            Replacement or exchange will be done in the same club location where the item was
            purchased.
          </li>
          <li>
            All defective merchandise for replacement or exchange must be returned within 7 days
            from the date of purchase along with the original S&R Membership Shopping sales
            receipt.
          </li>
          <li>
            Non-food merchandise must be returned complete with original packaging, manual and
            factory sales intact.
          </li>
          <li>Damaged or altered merchandise cannot be returned.</li>
          <li>Undergarments are non-returnable nor exchangeable.</li>
          <li>
            Certain items may be designated as "Service Center" items, and these are
            non-refundable and will be returned to an authorized Service Center for repair under
            manufacturer's warranty.
          </li>
          <li>
            For perishable items, replacement or exchange will only be done within 24 hours from
            date of purchase.
          </li>
          <li>For food items, at least 75% of the item must be returned.</li>
        </ul>
      </Section>
      <Section title="7. Warranty Policy">
        <p>
          S&R Membership Shopping honors all manufacturer's warranties. Merchandise must be
          returned with proof of purchase and registration certificate as applicable to a local
          manufacturer. The S&R Membership Shopping Return/Warranty Policy does not apply to any
          product, or part thereof, which has been damaged through: electrical voltage
          fluctuation, improper installation, misuse, alteration, attempts of repair, neglect,
          accident, removal/defacement of factory seals or serial numbers, damages from fire,
          flood, wind or water.
        </p>
      </Section>
      <Section title="8. General Policies">
        <p>
          Members are welcome to bring their children and up to 3 guests to S&R Membership
          Shopping. However, only members can purchase merchandise using his/her membership
          card. Parents are responsible for their children and should not leave them unattended.
        </p>
        <p>
          Opening of merchandise prior to purchase is not allowed. Members are responsible for
          any opened or damaged merchandise by their guests, children or themselves. S&R
          reserves the right to inspect purses, backpacks, diaper bags or briefcases. Shirts and
          shoes must be worn at all times.
        </p>
        <p>
          To make sure all members have been charged correctly for the merchandise received, all
          receipts will be checked upon leaving the club.
        </p>
        <p>
          S&R Membership Shopping is not responsible for any damage or loss caused, directly or
          indirectly, by defects in the merchandise manufactured or sold by other companies.
        </p>
        <p>
          S&R Membership Shopping does not assume the obligation of a depository or safekeeper
          of any vehicle and any content or part thereof while inside the S&R parking area. The
          member solely assumes all risks to his/her vehicle and its content when using the
          parking area though S&R Membership Shopping security personnel are posted thereat. S&R
          is not, therefore, responsible for damage or loss of the vehicle while at the parking
          area.
        </p>
        <p>
          By submitting an application form for membership, the applicant warrants that he/she
          has read and understood and gives his/her unqualified conformity to these terms and
          conditions of membership.
        </p>
      </Section>
    </>
  );
}

function Privacy() {
  return (
    <>
      <p>
        The privacy and security of personal data collected from you - our members, suppliers,
        and trade partners - are important to S&R. The officers and employees of S&R adhere to
        the principles of Data Privacy Act (R.A. 10173) and comply with the best data privacy
        practices sanctioned in its Implementing Rules and Regulations and Memorandum Circulars
        issued by the National Privacy Commission.
      </p>
      <Section title="Collection of Personal Data">
        <p>
          In the ordinary course of business, S&R may collect your personal data such as your
          name, address, contact details, gender, marital status, age, religious affiliation,
          health, education, and other personal information.
        </p>
      </Section>
      <Section title="Use of Personal Data">
        <p>
          We use this data to facilitate our transaction, administer your account with us, and
          most especially, to be more efficient in providing you with our products and services.
        </p>
      </Section>
      <Section title="Non-disclosure of Personal Data">
        <p>
          S&R will not disclose your personal information without your consent or in any
          circumstance not authorized by law or any valid order of court or government agencies.
        </p>
      </Section>
      <Section title="Security of Personal Data">
        <p>
          S&R applies strict security measures and modern technologies to make sure your
          personal data are kept confidential and secured with us. Our employees are trained to
          handle your personal data with utmost confidentiality and we have internal control in
          place to avoid security breach or violation.
        </p>
      </Section>
      <Section title="Use of Cookies">
        <p>
          In visiting our website, cookies are utilized. Cookies are small files that our site
          transfers to your computer or smart phones through your web browser that enables the
          site's system to recognize or remember certain information.
        </p>
        <p>
          The information gathered is used for statistical and analytical purposes. We may also
          use your browsing behavior to develop promotional offers, products, and services that
          we feel are beneficial to you.
        </p>
        <p>
          Cookies help us provide you with a better service by enabling us to understand your
          preferences based on previous or current website activity. However, a cookie gives us
          no access to your computer or any information about you other than the data you choose
          to share with us.
        </p>
        <p>
          You can choose to accept or decline cookies. Most web browsers automatically accept
          cookies. You can modify your browser setting to decline cookies if you prefer, but
          this may prevent you from taking full advantage of the website.
        </p>
        <p>
          By continuing to visit or use our website, you are agreeing to the use of cookies and
          similar technologies for the purposes we describe in this policy.
        </p>
      </Section>
      <Section title="Data Privacy Rights">
        <p>
          You may inquire or request for information regarding any matter relating to the
          processing of your personal data including the data privacy and security policies
          implemented by S&R to ensure the protection of your personal data.
        </p>
        <p>
          You may also request for an updating, rectification or amendment of your personal
          data, or file complaint in case of any breach or violation of your privacy.
        </p>
        <p>You may send your inquiry or request to the following:</p>
        <p>
          <strong>Email:</strong> dataprivacy@snrshopping.com
        </p>
        <p>
          <strong>Letter:</strong> Office of the Data Privacy Officer, S&R Membership Shopping,
          32nd Street cor. 5th Ave., Bonifacio Global City, Taguig City
        </p>
        <p>
          <strong>Call:</strong> (632) 8888 0433 loc. 109
        </p>
      </Section>
    </>
  );
}

function Reasons() {
  return (
    <>
      <p>
        S&R Membership Shopping respects its members' right to privacy. We only make use of your
        personal information for the following reasons:
      </p>
      <ol>
        <li>To administer your account with us.</li>
        <li>To facilitate your request from us.</li>
        <li>To be more efficient in providing you high-quality products and services.</li>
        <li>To communicate marketing promotions, offers, services and events better.</li>
      </ol>
    </>
  );
}
